import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useChild } from "../lib/childContext";
import { toast } from "sonner";
import ChildSwitcher from "../components/ChildSwitcher";
import AmbientDot from "../components/AmbientDot";
import SafeImage from "../components/SafeImage";
import { Send, MapPin, Tablet, Sparkles, X, Camera } from "lucide-react";

const MOODS = ["calm", "playful", "curious", "restful", "social", "creative"];
const STATES = ["arrived", "playing", "resting", "eating", "learning", "safe"];

export default function Caregiver() {
  const { selected, upsertSelected } = useChild();
  const [moments, setMoments] = useState([]);
  const [zones, setZones] = useState([]);
  const [text, setText] = useState("");
  const [mood, setMood] = useState("calm");
  const [photoFile, setPhotoFile] = useState(null);  // local File before upload
  const [photoUrl, setPhotoUrl] = useState(null);  // server URL after upload
  const [photoLocalPreview, setPhotoLocalPreview] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stateBusy, setStateBusy] = useState(false);
  const [zoneBusy, setZoneBusy] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [promptBusy, setPromptBusy] = useState(false);

  const askForPrompt = async () => {
    if (!selected) return;
    setPromptBusy(true);
    try {
      const r = await api.get(`/children/${selected.id}/moment-prompt`);
      setPrompt(r.data.prompt);
    } catch (err) {
      toast(err.response?.data?.detail || "No prompt to offer right now.");
    } finally {
      setPromptBusy(false);
    }
  };

  const usePrompt = () => {
    if (!prompt) return;
    // Use the prompt as a soft starter — caregivers can edit fully.
    setText(prompt.replace(/^Would you like to share /i, "").replace(/\?$/, ""));
    setPrompt(null);
  };

  useEffect(() => {
    if (!selected) {
      setMoments([]);
      setZones([]);
      return;
    }
    api.get(`/children/${selected.id}/moments`).then((r) => setMoments(r.data));
    if (selected.workspace_id) {
      api
        .get(`/workspaces/${selected.workspace_id}/zones`)
        .then((r) => setZones(r.data))
        .catch(() => setZones([]));
    } else {
      setZones([]);
    }
  }, [selected]);

  const post = async (e) => {
    e.preventDefault();
    if (!selected || !text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/children/${selected.id}/moments`, {
        text: text.trim(),
        mood,
        photo_url: photoUrl || null,
      });
      setMoments([r.data, ...moments]);
      setText("");
      setPhotoFile(null);
      setPhotoUrl(null);
      if (photoLocalPreview) {
        URL.revokeObjectURL(photoLocalPreview);
        setPhotoLocalPreview(null);
      }
      toast("Shared, quietly.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't share that just now.");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    const local = URL.createObjectURL(file);
    setPhotoLocalPreview(local);
    setPhotoFile(file);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post("/uploads/photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(r.data.url);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't upload that photo.");
      setPhotoFile(null);
      setPhotoUrl(null);
      URL.revokeObjectURL(local);
      setPhotoLocalPreview(null);
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = () => {
    if (photoLocalPreview) URL.revokeObjectURL(photoLocalPreview);
    setPhotoFile(null);
    setPhotoUrl(null);
    setPhotoLocalPreview(null);
  };

  const setState = async (next) => {
    if (!selected) return;
    setStateBusy(true);
    try {
      const r = await api.patch(`/children/${selected.id}/state`, { state: next });
      upsertSelected(r.data);
      toast(`${r.data.name} is ${next}.`);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't update just now.");
    } finally {
      setStateBusy(false);
    }
  };

  const moveToZone = async (zoneId) => {
    if (!selected) return;
    setZoneBusy(true);
    try {
      const r = await api.patch(`/children/${selected.id}/zone`, { zone_id: zoneId });
      upsertSelected(r.data);
      const z = zones.find((x) => x.id === zoneId);
      toast(zoneId ? `${r.data.name} is now in ${z?.name}.` : `${r.data.name} stepped away.`);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't move just now.");
    } finally {
      setZoneBusy(false);
    }
  };

  const triggerCircleOfCalm = async () => {
    if (!selected?.workspace_id) return;
    try {
      await api.post(`/workspaces/${selected.workspace_id}/circle-of-calm`, {});
      toast("A circle of calm has rippled out.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't send right now.");
    }
  };

  if (!selected) {
    return (
      <div className="min-h-screen">
        <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
          <div className="mx-auto max-w-3xl px-5 sm:px-8 py-4">
            <ChildSwitcher />
          </div>
        </header>
        <main className="mx-auto max-w-3xl px-5 sm:px-10 pt-12 pb-24">
          <span className="eyebrow">Share</span>
          <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight">
            No one to hold yet.
          </h1>
          <p className="mt-8 text-lg text-[#6A6863] font-light">
            A parent will connect you when the time is right. Until then, all is still.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-4">
          <ChildSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-6 pb-12 fade-up">
        <span className="eyebrow">Share, gently</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          A line about{" "}
          <em className="italic text-[#8A9984]">{selected.name}.</em>
        </h1>
        <p className="mt-5 text-base text-[#6A6863] font-light max-w-xl">
          Small notes, written as they happen. The parent receives them as an unhurried letter, not a notification.
        </p>

        {/* Kiosk mode handoff — child participation */}
        <section
          data-testid="caregiver-kiosk-cta"
          className="mt-10 rounded-3xl border hairline bg-white p-6 sm:p-8"
        >
          <div className="flex items-center gap-3">
            <Tablet size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">For {selected.name}</span>
          </div>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
            A quiet kiosk, just for them.
          </h2>
          <p className="mt-3 text-base text-[#6A6863] font-light max-w-xl">
            Hand the tablet to {selected.name}. They&rsquo;ll see a calm page with eight gentle
            icons — happy, sleepy, curious, a hug. No text labels they need to read. Their tap
            comes back to the parent as a soft note.
          </p>
          <Link
            to={`/with/${selected.id}`}
            data-testid="caregiver-open-kiosk"
            className="btn-primary mt-7 inline-flex"
          >
            Open kiosk for {selected.name}
            <Tablet size={14} strokeWidth={1.5} />
          </Link>
        </section>

        {/* Ambient state */}
        <section className="mt-12 rounded-3xl border hairline bg-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <AmbientDot state={selected.current_state} size={10} />
            <span className="eyebrow">Right now</span>
          </div>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl italic">
            {selected.name} is {selected.current_state}.
          </h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {STATES.map((s) => (
              <button
                key={s}
                onClick={() => setState(s)}
                disabled={stateBusy}
                data-testid={`state-${s}`}
                className={`rounded-full px-4 py-2 text-sm tracking-wide transition-colors duration-300 border ${
                  selected.current_state === s
                    ? "bg-[#9BA995] text-white border-[#9BA995]"
                    : "bg-transparent text-[#2C2B29] border-[#2C2B29]/10 hover:bg-[#F2EFEA]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </section>

        {/* Zone picker — only when child belongs to a workspace */}
        {selected.workspace_id && zones.length > 0 && (
          <section
            data-testid="caregiver-zones"
            className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8"
          >
            <div className="flex items-center gap-3">
              <MapPin size={14} strokeWidth={1.5} className="text-[#8A9984]" />
              <span className="eyebrow">Where</span>
            </div>
            <h2 className="mt-4 font-serif text-2xl italic">
              {selected.current_zone_label || `${selected.name} hasn't been placed yet.`}
            </h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {zones.map((z) => (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => moveToZone(z.id)}
                  disabled={zoneBusy}
                  data-testid={`zone-${z.id}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm tracking-wide transition-colors duration-300 border ${
                    selected.current_zone_id === z.id
                      ? "border-[#9BA995]"
                      : "border-[#2C2B29]/10 hover:bg-[#F2EFEA]"
                  }`}
                  style={
                    selected.current_zone_id === z.id
                      ? { background: z.color, color: "#fff", borderColor: z.color }
                      : {}
                  }
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: z.color }}
                  />
                  {z.name}
                </button>
              ))}
              {selected.current_zone_id && (
                <button
                  type="button"
                  onClick={() => moveToZone(null)}
                  disabled={zoneBusy}
                  data-testid="zone-clear"
                  className="rounded-full px-4 py-2 text-sm text-[#6A6863] border border-[#2C2B29]/10 hover:bg-[#F2EFEA]"
                >
                  step away
                </button>
              )}
            </div>
            <div className="mt-7 pt-6 border-t hairline">
              <button
                type="button"
                onClick={triggerCircleOfCalm}
                data-testid="caregiver-circle-of-calm"
                className="btn-ghost"
              >
                Send a circle of calm
              </button>
              <p className="mt-3 text-xs text-[#6A6863]">
                A single, gentle reassurance is sent to every parent right now.
              </p>
            </div>
          </section>
        )}

        {/* Post a moment */}
        <form
          onSubmit={post}
          className="mt-10 rounded-3xl border hairline bg-[#F2EFEA]/50 p-6 sm:p-8"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="eyebrow">A moment</label>
            <button
              type="button"
              onClick={askForPrompt}
              disabled={promptBusy}
              data-testid="caregiver-ask-prompt"
              className="btn-ghost !py-1.5 !px-3.5 !text-[10px]"
            >
              {promptBusy ? "Listening…" : "A gentle suggestion"}
              <Sparkles size={12} strokeWidth={1.5} />
            </button>
          </div>
          {prompt && (
            <div
              data-testid="caregiver-prompt-bubble"
              className="mt-5 rounded-2xl bg-white border hairline p-5 flex items-start gap-4"
            >
              <div className="flex-1">
                <div className="eyebrow">Alouvya suggests</div>
                <p className="mt-2 font-serif italic text-lg text-[#2C2B29] leading-snug">
                  {prompt}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={usePrompt}
                    data-testid="caregiver-prompt-use"
                    className="btn-ghost !py-1.5 !px-3.5 !text-[10px]"
                  >
                    Use as a starter
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrompt(null)}
                    data-testid="caregiver-prompt-dismiss"
                    className="text-xs text-[#6A6863] hover:text-[#2C2B29] transition-colors px-3 py-2"
                  >
                    Not now
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPrompt(null)}
                data-testid="caregiver-prompt-close"
                aria-label="Dismiss"
                className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-1"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="e.g. Leo helped water the plant by the window."
            data-testid="caregiver-moment-text"
            className="orbit-input mt-4 resize-none"
          />
          <div className="mt-7">
            <label className="eyebrow">Photo (optional)</label>
            <div className="mt-3" data-testid="caregiver-photo-area">
              {photoLocalPreview || photoUrl ? (
                <div className="relative inline-block">
                  <SafeImage
                    src={photoLocalPreview || photoUrl}
                    alt="Selected"
                    data-testid="caregiver-photo-preview"
                    className="rounded-2xl max-h-48 object-cover border hairline"
                  />
                  {photoBusy && (
                    <div
                      data-testid="caregiver-photo-uploading"
                      className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl"
                    >
                      <span className="font-serif italic text-[#6A6863] text-sm">
                        Uploading gently…
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={removePhoto}
                    data-testid="caregiver-photo-remove"
                    aria-label="Remove photo"
                    className="absolute top-2 right-2 bg-white text-[#6A6863] hover:text-[#2C2B29] rounded-full p-1 border hairline shadow"
                  >
                    <X size={14} strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <label
                  data-testid="caregiver-photo-input-label"
                  className="inline-flex items-center gap-2 rounded-full bg-[#F2EFEA] border hairline px-4 py-2 text-xs tracking-[0.18em] uppercase text-[#6A6863] cursor-pointer hover:text-[#2C2B29] hover:bg-white transition-colors"
                >
                  <Camera size={12} strokeWidth={1.5} />
                  Add a photo
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                    data-testid="caregiver-photo-input"
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="mt-7">
            <label className="eyebrow">Mood</label>
            <div className="mt-4 flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMood(m)}
                  data-testid={`mood-${m}`}
                  className={`rounded-full px-4 py-2 text-xs tracking-widest uppercase transition-colors duration-300 border ${
                    mood === m
                      ? "bg-[#E6BFA6] text-[#2C2B29] border-[#E6BFA6]"
                      : "bg-transparent text-[#6A6863] border-[#2C2B29]/10 hover:bg-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-8">
            <button
              type="submit"
              disabled={busy || !text.trim()}
              data-testid="caregiver-share-btn"
              className="btn-primary"
            >
              {busy ? "Sharing…" : "Share gently"}
              <Send size={14} strokeWidth={1.5} />
            </button>
          </div>
        </form>

        {/* Recent */}
        <section className="mt-16">
          <span className="eyebrow">Recently shared</span>
          <ol className="mt-6 space-y-8">
            {moments.slice(0, 6).map((m, i) => (
              <li
                key={m.id}
                data-testid={`caregiver-moment-${i}`}
                className="border-l hairline pl-5"
              >
                <p className="font-serif text-lg sm:text-xl text-[#2C2B29] leading-snug">
                  {m.text}
                </p>
                <p className="mt-2 text-xs text-[#6A6863] tracking-wide">
                  {m.mood} ·{" "}
                  {new Date(m.created_at).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </li>
            ))}
            {moments.length === 0 && (
              <li className="font-serif italic text-[#6A6863]">
                Nothing shared yet.
              </li>
            )}
          </ol>
        </section>
      </main>
    </div>
  );
}
